import Image from 'next/image';
import { useIntl } from 'react-intl';

import { Box } from '@/components/Box';
import { LegacyButton } from '@/components/LegacyButton';
import { useRouter } from '@/hooks/useRouter';
import { paths } from '@/lib/paths';
import { commonMessages } from '@/messages/common';

import type { NewsPageType } from '../../types';

import LogoSvg from '@/assets/breitling-simple.svg';

import * as S from './ArticlePreview.styles';

type ArticlePreviewProps = {
  cardClassName?: string;
  className?: string;
  data: NewsPageType | null;
  deskHeight?: number;
  height?: number;
  imgClassName?: string;
  infoClassName?: string;
  showButton?: boolean;
};

export const ArticlePreview = ({
  cardClassName,
  className,
  data,
  deskHeight,
  height,
  imgClassName,
  infoClassName,
  showButton,
}: ArticlePreviewProps) => {
  const { formatMessage } = useIntl();
  const thumbnailImage = data?.thumbnail?.image?.url;
  const router = useRouter();

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (data?.slug) {
      router.push(paths.newsArticle.asPath({ slug: data?.slug }));
    }
  };

  return (
    <S.StyledLink
      className={className}
      data-testid='styledLine'
      onClick={handleClick}
    >
      <S.ArticleCard className={cardClassName}>
        <S.ImageWrapper
          $deskHeight={deskHeight}
          $height={height}
          className={imgClassName}
        >
          <Image
            alt={data?.thumbnail?.alt || data?.thumbnail?.image?.title || ''}
            layout='fill'
            objectFit='cover'
            src={thumbnailImage ?? LogoSvg}
          />
        </S.ImageWrapper>
        <Box className={infoClassName}>
          <S.ArticleTag>
            {data?.tagsCollection?.items &&
              data.tagsCollection?.items?.length > 0 && (
                <S.InlineList>
                  {data?.tagsCollection.items.map((tag, index) => (
                    <S.InlineListItem key={index}>
                      {tag?.value || 'Category'}
                    </S.InlineListItem>
                  ))}
                </S.InlineList>
              )}
          </S.ArticleTag>

          <S.ArticleTitle>{data?.pageTitle}</S.ArticleTitle>
          <S.ArticlePreviewTextWrapper>
            <S.ArticlePreviewText>
              {data?.shortDescription}
            </S.ArticlePreviewText>
          </S.ArticlePreviewTextWrapper>
          {showButton && (
            <S.ButtonWrapper>
              <LegacyButton onClick={handleClick}>
                {formatMessage(commonMessages.readMore)}
              </LegacyButton>
            </S.ButtonWrapper>
          )}
        </Box>
      </S.ArticleCard>
    </S.StyledLink>
  );
};

export default ArticlePreview;
